import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, marketplaceApi, playlistApi } from '../utils/api';

const TERMS_AND_CONDITIONS = (sellerName) => `TERMS AND CONDITIONS FOR SUBMISSION TO CHESSFLASHCARDS.ORG

These Terms and Conditions ("Agreement") govern the submission of flashcard content to ChessFlashcards.org, LLC ("CHESSFLASHCARDS").

1. LICENSE GRANT
${sellerName} ("SELLER") hereby grants to CHESSFLASHCARDS a perpetual, irrevocable, worldwide, royalty-free, non-exclusive license to use, reproduce, distribute, display, perform, sublicense, and create derivative works of all flashcards submitted under this Agreement. This license is unrestricted and includes the right to grant sublicenses to end users of the ChessFlashcards.org platform.

2. INTELLECTUAL PROPERTY WARRANTY
SELLER represents and warrants that:
   (a) SELLER is the sole original creator of all submitted flashcards and the content therein;
   (b) the submitted flashcards do not infringe upon, misappropriate, or violate any third party's intellectual property rights, including but not limited to copyrights, trademarks, trade secrets, or patents;
   (c) the submitted flashcards do not reproduce, derive from, or constitute a substantial copy of any other person's or entity's copyrighted material without express written permission;
   (d) SELLER has full right, power, and authority to enter into this Agreement and to grant the rights and licenses described herein.

3. COMPENSATION
CHESSFLASHCARDS agrees to pay SELLER the sale price entered at the time of submission for each end-user purchase of the submitted flashcard deck, subject to applicable platform fees and payment processing charges as published on ChessFlashcards.org from time to time.

4. REVIEW AND APPROVAL
CHESSFLASHCARDS reserves the right to review, approve, reject, modify, or remove any submitted content at its sole discretion. Submission does not guarantee publication on the marketplace.

5. INDEMNIFICATION
SELLER agrees to indemnify, defend, and hold harmless CHESSFLASHCARDS, its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to any breach of this Agreement or the warranties made herein.

6. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of the applicable jurisdiction, without regard to conflict of law principles.

By clicking "Agree and Submit," SELLER acknowledges that SELLER has read, understood, and agrees to be bound by these Terms and Conditions.`;

export default function PlaylistSubmission() {
  const { id }         = useParams();
  const { token, user } = useAuth();
  const navigate        = useNavigate();

  const [playlist, setPlaylist]   = useState(null);
  const [cards, setCards]         = useState([]);
  const [price, setPrice]         = useState('');
  const [agreed, setAgreed]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pl    = await playlistApi.get(id, token);
      setPlaylist(pl);
      const items = (pl.items || []).slice().sort((a, b) => a.sort_order - b.sort_order);
      const fetched = await Promise.all(
        items.map(item => flashcardApi.get(item.flashcard_id, token).catch(() => null))
      );
      setCards(fetched.filter(Boolean));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const sellerName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email_address || 'SELLER'
    : 'SELLER';

  const handlePriceChange = (e) => {
    // Allow only digits and a single decimal point, max 2 decimal places
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setPrice(raw);
  };

  const handleSubmit = async () => {
    const parsedPrice = parseFloat(price);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      alert('Please enter a valid price (e.g. 2.99).');
      return;
    }
    if (!agreed) {
      alert('You must agree to the Terms and Conditions before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await marketplaceApi.submit(id, parsedPrice, token);
      alert('Your playlist has been submitted to the marketplace for review!');
      navigate('/playlists');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  return (
    <>
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Publish Playlist</h2>
        <button className="btn btn-link" onClick={() => navigate('/playlists')}>
          ← Back to Playlists
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Playlist header — read-only */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header fw-semibold">Playlist Details</div>
        <div className="card-body">
          <dl className="row mb-0">
            <dt className="col-sm-3">Title</dt>
            <dd className="col-sm-9">{playlist?.title}</dd>
            <dt className="col-sm-3">Cards</dt>
            <dd className="col-sm-9">{cards.length}</dd>
          </dl>
        </div>
      </div>

      {/* Scrollable flashcard list — one row high per card */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header fw-semibold">Flashcards in this Playlist</div>
        <div
          className="card-body p-0"
          style={{ maxHeight: '14rem', overflowY: 'auto' }}
        >
          {cards.length === 0 ? (
            <div className="p-3 text-muted">No flashcards in this playlist.</div>
          ) : (
            <ul className="list-group list-group-flush">
              {cards.map((card, idx) => (
                <li
                  key={card.id}
                  className="list-group-item py-1 px-3 d-flex align-items-center gap-2"
                  style={{ minHeight: 0 }}
                >
                  <span className="text-muted small" style={{ minWidth: '1.5rem' }}>{idx + 1}.</span>
                  <span className="small">{card.name}</span>
                  {card.problem && (
                    <span className="text-muted small ms-auto text-truncate" style={{ maxWidth: '50%' }}>
                      {card.problem}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Price field */}
      <div className="mb-4" style={{ maxWidth: '16rem' }}>
        <label className="form-label fw-semibold" htmlFor="sale-price">
          Your sale price
        </label>
        <div className="input-group">
          <span className="input-group-text">$</span>
          <input
            id="sale-price"
            type="text"
            inputMode="decimal"
            className="form-control"
            placeholder="2.99"
            value={price}
            onChange={handlePriceChange}
          />
        </div>
      </div>

      {/* Terms & Conditions box */}
      <div className="card mb-3 shadow-sm">
        <div className="card-header fw-semibold">Terms and Conditions</div>
        <div
          className="card-body"
          style={{ maxHeight: '16rem', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}
        >
          {TERMS_AND_CONDITIONS(sellerName)}
        </div>
      </div>

      {/* Agreement checkbox */}
      <div className="form-check mb-4">
        <input
          className="form-check-input"
          type="checkbox"
          id="chk-agree"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="chk-agree">
          I have read and agree to the Terms and Conditions
        </label>
      </div>

      {/* Action buttons */}
      <div className="d-flex gap-2">
        <button
          className="btn btn-success btn-lg"
          onClick={handleSubmit}
          disabled={submitting || !agreed || cards.length === 0}
        >
          {submitting ? 'Submitting…' : 'Agree and Submit'}
        </button>
        <button
          className="btn btn-outline-secondary btn-lg"
          onClick={() => navigate('/playlists')}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </>
  );
}
